import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  CircularProgress, 
  Alert,
  Chip,
  Divider,
  Button,
  TextField,
  InputAdornment,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { useParams } from "react-router-dom";
import axios from 'axios';
import { API_URL } from './constants';
import { getResolvedProductDisplayImageUrl } from './productUtils';
import { ProductImageBox } from './ProductImageBox';
import { 
  Package, 
  Tag, 
  Star, 
  CurrencyDollar, 
  MapPin,
  Storefront,
  Image as ImageIcon,
  Factory
} from '@phosphor-icons/react';
import { Search } from '@mui/icons-material';

function ProductDetailViewUnified() {
  const { projectKey } = useParams();
  
  // State for data
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for filters
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedManufacturer, setSelectedManufacturer] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [productSearchTerm, setProductSearchTerm] = useState('');
  
  // State for unique values
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // State for selected product details (all matching records)
  const [selectedProductDetails, setSelectedProductDetails] = useState(null);
  const [allProductRecords, setAllProductRecords] = useState([]);
  const [productImageSrc, setProductImageSrc] = useState(null);
  
  // State for filtered products count
  const [filteredProductsCount, setFilteredProductsCount] = useState(0);

  // Fetch data from the Product Reference API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/product-reference-data`);
        
        const { columns: apiColumns, data: apiData } = response.data;
        
        // Convert raw data to objects with column names
        const processedData = apiData.map(row => {
          const obj = {};
          apiColumns.forEach((col, index) => {
            obj[col] = row[index];
          });
          return obj;
        });
        
        setData(processedData);
        
        // Extract unique values - using the new column names
        const uniqueBrands = [...new Set(processedData.map(item => item.brand).filter(Boolean))].sort();
        const uniqueManufacturers = [...new Set(processedData.map(item => item.manufacturer).filter(Boolean))].sort();
        const uniqueCategories = [...new Set(processedData.map(item => item.category).filter(Boolean))].sort();
        
        setBrands(uniqueBrands);
        setManufacturers(uniqueManufacturers);
        setCategories(uniqueCategories);
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Update products when filters change
  useEffect(() => {
    // First, apply filters to get filtered data
    let filteredData = [...data];
    
    if (selectedBrand) {
      filteredData = filteredData.filter(item => item.brand === selectedBrand);
    }
    if (selectedManufacturer) {
      filteredData = filteredData.filter(item => item.manufacturer === selectedManufacturer);
    }
    if (selectedCategory) {
      filteredData = filteredData.filter(item => item.category === selectedCategory);
    }
    
    // Get products from filtered data (using Title column)
    const filteredProducts = filteredData
      .map(item => item.Title)
      .filter(Boolean);
    
    const uniqueProducts = [...new Set(filteredProducts)].sort();
    
    if (productSearchTerm) {
      // When searching, filter products by search term from the already filtered list
      const searchResults = uniqueProducts.filter(product => 
        product.toLowerCase().includes(productSearchTerm.toLowerCase())
      );
      setProducts(searchResults);
      setFilteredProductsCount(searchResults.length);
    } else {
      // When not searching, show all products from filtered data
      setProducts(uniqueProducts);
      setFilteredProductsCount(uniqueProducts.length);
    }
    
  }, [selectedBrand, selectedManufacturer, selectedCategory, productSearchTerm, data]);

  // Update product details when product changes
  useEffect(() => {
    if (selectedProduct) {
      let filteredData = data.filter(item => item.Title === selectedProduct);
      
      // Apply available filters (all optional)
      if (selectedBrand) {
        filteredData = filteredData.filter(item => item.brand === selectedBrand);
      }
      if (selectedManufacturer) {
        filteredData = filteredData.filter(item => item.manufacturer === selectedManufacturer);
      }
      if (selectedCategory) {
        filteredData = filteredData.filter(item => item.category === selectedCategory);
      }
      
      // If no data after filtering, try without filters (for search results)
      if (filteredData.length === 0) {
        filteredData = data.filter(item => item.Title === selectedProduct);
      }
      
      // Store all matching records (for showing all retailers)
      if (filteredData.length > 0) {
        setAllProductRecords(filteredData);
        // Use first record for product characteristics (they should be the same)
        setSelectedProductDetails(filteredData[0]);
      } else {
        setAllProductRecords([]);
        setSelectedProductDetails(null);
      }
    } else {
      setSelectedProductDetails(null);
    }
  }, [selectedBrand, selectedProduct, selectedManufacturer, selectedCategory, data]);

  const handleBrandChange = (event) => {
    setSelectedBrand(event.target.value);
  };

  const handleProductChange = (event) => {
    setSelectedProduct(event.target.value);
  };

  const handleManufacturerChange = (event) => {
    setSelectedManufacturer(event.target.value);
    if (!selectedProduct) {
      setSelectedProductDetails(null);
    }
    if (!productSearchTerm) {
      setProductSearchTerm('');
    }
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    if (!selectedProduct) {
      setSelectedProductDetails(null);
    }
    if (!productSearchTerm) {
      setProductSearchTerm('');
    }
  };

  const clearAllFilters = () => {
    setSelectedBrand('');
    setSelectedProduct('');
    setSelectedManufacturer('');
    setSelectedCategory('');
    setProductSearchTerm('');
    setSelectedProductDetails(null);
    setAllProductRecords([]);
  };

  useEffect(() => {
    if (!selectedProductDetails) {
      setProductImageSrc(null);
      return;
    }
    setProductImageSrc(getResolvedProductDisplayImageUrl(selectedProductDetails));
  }, [selectedProductDetails]);

  // Helper function to check if value is a date and return null if so
  const isValidPrice = (value) => {
    if (value === null || value === undefined || value === 'N/A') {
      return null;
    }
    
    // Check if it's a date string (ISO format or date-like)
    const datePattern = /^\d{4}-\d{2}-\d{2}/;
    if (typeof value === 'string' && datePattern.test(value)) {
      return null; // It's a date, not a price
    }
    
    // Check if it's a Date object
    if (value instanceof Date) {
      return null; // It's a date, not a price
    }
    
    // Check if it's a valid number
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    if (!isNaN(numValue) && isFinite(numValue)) {
      return numValue;
    }
    
    return null; // Not a valid price
  };

  const extractProductInfo = (productData) => {
    if (!productData) return {};
    
    // Get price values and validate them
    const unitPriceRaw = productData['Unit price'] || productData['Unit price_enrichment'];
    const actualPriceRaw = productData['Actual price'] || productData['Actual price_enrichment'];
    const lowestPriceRaw = productData['Lowest unit price'] || productData['Lowest unit price_enrichment'];
    
    return {
      category: productData.category || 'N/A',
      manufacturer: productData.manufacturer || 'N/A',
      format: productData.format || 'N/A',
      is_mixpack: productData['Is mixpack'] || 'N/A',
      units_in_mixpack: productData['# units in mixpack'] || 'N/A',
      content_package_g: productData.content_package_g || 'N/A',
      portions_in_package: productData.portions_in_package || 'N/A',
      weight_serving_g: productData.weight_serving_g || 'N/A',
      ean: productData.EAN || 'N/A',
      // Enrichment data from last day dashboard - validate prices to exclude dates
      unit_price: isValidPrice(unitPriceRaw) !== null ? isValidPrice(unitPriceRaw) : 'N/A',
      actual_price: isValidPrice(actualPriceRaw) !== null ? isValidPrice(actualPriceRaw) : 'N/A',
      lowest_unit_price: isValidPrice(lowestPriceRaw) !== null ? isValidPrice(lowestPriceRaw) : 'N/A',
      units: productData['Units (#)'] || productData['Units (#)_enrichment'] || 'N/A',
      currency: productData.Currency || productData.Currency_enrichment || 'N/A',
      country: productData.Country || productData.Country_enrichment || 'N/A',
      retailer: productData.Retailer || productData.Retailer_enrichment || 'N/A',
      url: productData.Url || productData.Url_enrichment || 'N/A',
      options: productData.Options || productData.Options_enrichment || 'N/A',
      date: productData.Date || productData.Date_enrichment || 'N/A',
      size_lowest_price: productData['Size with lowest unit price'] || productData['Size with lowest unit price_enrichment'] || 'N/A'
    };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const productInfo = extractProductInfo(selectedProductDetails);

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Product Detail View
      </Typography>

      {/* Selection Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">
              Select Product
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              {selectedBrand && (
                <Typography variant="body2" color="text.secondary">
                  {filteredProductsCount} product{filteredProductsCount !== 1 ? 's' : ''} found
                </Typography>
              )}
              <Button 
                variant="outlined" 
                color="secondary" 
                onClick={clearAllFilters}
                size="small"
              >
                Clear All Filters
              </Button>
            </Box>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  label="Category"
                  onChange={handleCategoryChange}
                >
                  <MenuItem value="">
                    <em>All categories</em>
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Manufacturer</InputLabel>
                <Select
                  value={selectedManufacturer}
                  label="Manufacturer"
                  onChange={handleManufacturerChange}
                >
                  <MenuItem value="">
                    <em>Select a manufacturer</em>
                  </MenuItem>
                  {manufacturers.map((manufacturer) => (
                    <MenuItem key={manufacturer} value={manufacturer}>
                      {manufacturer}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Brand</InputLabel>
                <Select
                  value={selectedBrand}
                  label="Brand"
                  onChange={handleBrandChange}
                >
                  <MenuItem value="">
                    <em>Select a brand</em>
                  </MenuItem>
                  {brands.map((brand) => (
                    <MenuItem key={brand} value={brand}>
                      {brand}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                options={products}
                value={selectedProduct}
                onChange={(event, newValue) => {
                  setSelectedProduct(newValue || '');
                  if (newValue) {
                    setProductSearchTerm(newValue);
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  setProductSearchTerm(newInputValue);
                  if (!newInputValue) {
                    setSelectedProduct('');
                  }
                }}
                inputValue={productSearchTerm}
                openOnFocus
                autoHighlight
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Products"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    {option}
                  </Box>
                )}
                noOptionsText="No products found"
                freeSolo
                clearOnEscape
                selectOnFocus
                handleHomeEndKeys
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Product Details */}
      {selectedProductDetails ? (
        <Grid container spacing={3}>
          {/* Product Image */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <ImageIcon size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                  Product Image
                </Typography>
                {productImageSrc ? (
                  <ProductImageBox
                    src={productImageSrc}
                    alt={selectedProductDetails.Title}
                    size={160}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 160,
                      height: 160,
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      No image
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Product Information */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ color: '#1976d2' }}>
                  {selectedProductDetails.Title}
                </Typography>
                
                <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
                  <Chip 
                    icon={<Package size={16} />} 
                    label={selectedProductDetails.brand}
                    color="primary" 
                    variant="outlined"
                  />
                  <Chip 
                    icon={<Factory size={16} />} 
                    label={selectedProductDetails.manufacturer}
                    color="secondary" 
                    variant="outlined"
                  />
                  <Chip 
                    icon={<Tag size={16} />} 
                    label={selectedProductDetails.category}
                    color="success" 
                    variant="outlined"
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Product Characteristics */}
                <Typography variant="h6" gutterBottom>
                  Product Characteristics
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6} md={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Package size={16} color="#2196F3" />
                      <Typography variant="body2">
                        <strong>Category:</strong> {productInfo.category}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Tag size={16} color="#9C27B0" />
                      <Typography variant="body2">
                        <strong>Format:</strong> {productInfo.format}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Pricing Information */}
                <Typography variant="h6" gutterBottom>
                  Pricing Information
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2">
                      <strong>Unit Price:</strong> {productInfo.unit_price !== 'N/A' ? `${productInfo.unit_price} ${productInfo.currency}` : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2">
                      <strong>Actual Price:</strong> {productInfo.actual_price !== 'N/A' ? `${productInfo.actual_price} ${productInfo.currency}` : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2">
                      <strong>Lowest Unit Price:</strong> {productInfo.lowest_unit_price !== 'N/A' ? `${productInfo.lowest_unit_price} ${productInfo.currency}` : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2">
                      <strong>Units (#):</strong> {productInfo.units}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2">
                      <strong>Size with Lowest Price:</strong> {productInfo.size_lowest_price}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Retailer & Availability Information */}
                <Typography variant="h6" gutterBottom>
                  Retailer Availability ({allProductRecords.length} record{allProductRecords.length !== 1 ? 's' : ''})
                </Typography>
                {allProductRecords.length > 0 ? (
                  <TableContainer component={Paper} sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Retailer</strong></TableCell>
                          <TableCell><strong>Country</strong></TableCell>
                          <TableCell><strong>Unit Price</strong></TableCell>
                          <TableCell><strong>Actual Price</strong></TableCell>
                          <TableCell><strong>Lowest Unit Price</strong></TableCell>
                          <TableCell><strong>Units (#)</strong></TableCell>
                          <TableCell><strong>Currency</strong></TableCell>
                          <TableCell><strong>Last seen on date</strong></TableCell>
                          <TableCell><strong>URL</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allProductRecords.map((record, index) => {
                          const recordInfo = extractProductInfo(record);
                          
                          // Helper function to check if value is a date and format price
                          const formatPrice = (value) => {
                            if (value === 'N/A' || value === null || value === undefined) {
                              return 'N/A';
                            }
                            
                            // Check if it's a date string (ISO format or date-like)
                            const datePattern = /^\d{4}-\d{2}-\d{2}/;
                            const isDateString = typeof value === 'string' && datePattern.test(value);
                            
                            // Check if it's a Date object
                            const isDateObject = value instanceof Date;
                            
                            // If it's a date, return empty or N/A
                            if (isDateString || isDateObject) {
                              return '';
                            }
                            
                            // Check if it's a valid number
                            const numValue = typeof value === 'number' ? value : parseFloat(value);
                            if (!isNaN(numValue) && isFinite(numValue)) {
                              return numValue.toString();
                            }
                            
                            return 'N/A';
                          };
                          
                          return (
                            <TableRow key={index}>
                              <TableCell>{recordInfo.retailer}</TableCell>
                              <TableCell>{recordInfo.country}</TableCell>
                              <TableCell>{formatPrice(recordInfo.unit_price)}</TableCell>
                              <TableCell>{formatPrice(recordInfo.actual_price)}</TableCell>
                              <TableCell>{formatPrice(recordInfo.lowest_unit_price)}</TableCell>
                              <TableCell>{recordInfo.units}</TableCell>
                              <TableCell>{recordInfo.currency}</TableCell>
                              <TableCell>
                                {recordInfo.date !== 'N/A' ? (typeof recordInfo.date === 'string' ? recordInfo.date.split('T')[0] : recordInfo.date) : 'N/A'}
                              </TableCell>
                              <TableCell>
                                {recordInfo.url && recordInfo.url !== 'N/A' ? (
                                  <a
                                    href={recordInfo.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#1976d2' }}
                                  >
                                    Real
                                  </a>
                                ) : (
                                  'N/A'
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No retailer availability data available
                  </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Package Information */}
                <Typography variant="h6" gutterBottom>
                  Package Information
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2">
                      <strong>Is Mixpack:</strong> {productInfo.is_mixpack}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2">
                      <strong>Units in Mixpack:</strong> {productInfo.units_in_mixpack}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2">
                      <strong>Content Package (g):</strong> {productInfo.content_package_g}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2">
                      <strong>Portions in Package:</strong> {productInfo.portions_in_package}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2">
                      <strong>Weight Serving (g):</strong> {productInfo.weight_serving_g}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Additional Information */}
                <Typography variant="h6" gutterBottom>
                  Additional Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <strong>Product ID:</strong> {selectedProductDetails.id || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <strong>EAN:</strong> {productInfo.ean}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h6" color="text.secondary" align="center">
              {selectedBrand ? 'Select a product to view details' : 'Select filters and a product to view details'}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default ProductDetailViewUnified;

